// Music controls component - volume slider and mute toggle for background music

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { musicManager } from '../../lib/music-manager.js';

class MusicControls extends BaseComponent {
  static properties = {
    _volume: { type: Number, state: true },
    _muted: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: flex;
      position: absolute;
      bottom: 20px;
      left: 20px;
      z-index: 100;
    }

    .music-controls {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(0, 0, 0, 0.8);
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
    }

    .mute-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      color: #ccc;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: color 0.2s;
    }

    .mute-btn:hover {
      color: #fff;
    }

    .volume-slider {
      -webkit-appearance: none;
      appearance: none;
      width: 80px;
      height: 4px;
      background: #444;
      border-radius: 2px;
      outline: none;
      cursor: pointer;
    }

    .volume-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      background: #ccc;
      border-radius: 50%;
      cursor: pointer;
      transition: background 0.2s;
    }

    .volume-slider::-webkit-slider-thumb:hover {
      background: #fff;
    }

    .volume-slider::-moz-range-thumb {
      width: 14px;
      height: 14px;
      background: #ccc;
      border-radius: 50%;
      border: none;
      cursor: pointer;
    }
  `;

  constructor() {
    super();
    this._volume = musicManager.getVolume();
    this._muted = musicManager.isMuted();
  }

  _handleMuteToggle() {
    this._muted = !this._muted;
    musicManager.setMuted(this._muted);
  }

  /** @param {Event} e */
  _handleVolumeChange(e) {
    const input = /** @type {HTMLInputElement} */ (e.target);
    this._volume = parseFloat(input.value);
    musicManager.setVolume(this._volume);
    if (this._muted && this._volume > 0) {
      this._muted = false;
      musicManager.setMuted(false);
    }
  }

  _renderSpeakerIcon() {
    if (this._muted || this._volume === 0) {
      return html`<svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"
        />
        <line x1="22" x2="16" y1="9" y2="15" />
        <line x1="16" x2="22" y1="9" y2="15" />
      </svg>`;
    }
    return html`<svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path
        d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"
      />
      <path d="M16 9a5 5 0 0 1 0 6" />
      <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
    </svg>`;
  }

  render() {
    return html`
      <div class="music-controls">
        <button class="mute-btn" @click="${this._handleMuteToggle}" title="${this._muted ? 'Unmute' : 'Mute'}">
          ${this._renderSpeakerIcon()}
        </button>
        <input
          type="range"
          class="volume-slider"
          min="0"
          max="1"
          step="0.05"
          .value="${String(this._volume)}"
          @input="${this._handleVolumeChange}"
        />
      </div>
    `;
  }
}

customElements.define('music-controls', MusicControls);
